import AppConfig from 'app/config/AppConfig';
import {UpsalePopupStatus} from 'app/enums';
import { getRecaptchaAction, RECAPTCHA_ACTIONS } from 'components/services/google/recaptcha';
export function cleanUpObject(obj) {
    return JSON.parse(JSON.stringify(obj))
}


export function flattenCategories(categories, map = {}) {
    categories.forEach(category => {
        map[String(category.id)] = category;
        if (category.subCategories) {
            flattenCategories(category.subCategories, map);
        }
    });
    return map;         
}


// Combine multiple class-names to a string, and ignore falsely items
export function combineClassNames(...classNames) {
    return classNames.filter(Boolean).join(' ');
}

// Calculate the size in viewport width units based on the required size and device width
export function vwSize(size, width) {
    return size / width * 100;
}

// Change array keys to another unique value (id by default)
export function changeArrayKeys(originalArray, fieldToBeKey = 'id') {
    return originalArray.reduce((previousValue, currentValue) => {
        previousValue[currentValue[fieldToBeKey]] = currentValue;
        return previousValue;
    }, {});
}

// Parse response from API and convert data to another type
export function parseApiResponse(responseData, fieldsToParse = {}) {
    if (Array.isArray(responseData)) {
        return responseData.map((recordData) => parseApiResponse(recordData, fieldsToParse));
    }

    for (const fieldKey in fieldsToParse) {
        if (responseData[fieldKey]) {
            switch (fieldsToParse[fieldKey]) {
                case 'number':
                    responseData[fieldKey] = Number(responseData[fieldKey]);
                    break;

                // More types can be added as needed...
            }
        }
    }

    return responseData;
}

// Decide at which frame to start the slider
export function decideInitialSlide(itemsArray, itemsPerFrame, selectedItemId = 0) {
    // By default, the slider starts at the beginning
    let initialSlide = itemsArray.length - itemsPerFrame;

    // If there's a selected item, focus on it instead
    if (selectedItemId) {
        const reversedItemsArray = [...itemsArray].reverse();

        const selectedItemIndex = reversedItemsArray.findIndex(
            (itemData) => itemData.id === selectedItemId
        );

        // If nothing was found, return the current value (the beginning of the slider)
        if (selectedItemIndex === -1) {
            return initialSlide;
        }

        // Otherwise, search and return the correct frame of the found item
        while (selectedItemIndex < initialSlide && initialSlide !== 0) {
            initialSlide -= itemsPerFrame;

            if (initialSlide < 0) {
                initialSlide = 0;
            }
        }
    }

    return initialSlide;
}

// Show a data in a user-friendly format (by default: DD Month YYYY in Hebrew)
export function convertDateToReadableFormat(dateValue, local = 'he-IL', options = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
}) {
    return new Date(dateValue).toLocaleDateString(local, options);
}

// Handle parsing of searchFilters from URL
export function parseSearchFiltersFromUrl(filters) {
    const urlParams = window.location.search;

    const initialState = JSON.parse(JSON.stringify(filters))
    if (urlParams) {
        const parsedParams = parseUrlParams(
            urlParams.split('?')[1]
        );

        for (const paramKey in parsedParams) {
            if (paramKey in initialState) {
                // Make sure benefitType is an array
                if (paramKey === 'benefitType') {
                    if (!Array.isArray(parsedParams[paramKey])) {
                        parsedParams[paramKey] = [parsedParams[paramKey]];
                    }
                }

                // Handle textQuery value
                if (paramKey === 'textQuery') {
                    initialState[paramKey] = {
                        ...initialState[paramKey],
                        text: decodeURI(parsedParams[paramKey]),
                    };

                    continue;
                }

                // Handle array value
                if (Array.isArray(parsedParams[paramKey])) {
                    const idsList = parsedParams[paramKey].map(
                        (paramValue) => Number(paramValue)
                    ).filter(Number);

                    initialState[paramKey] = {
                        ...initialState[paramKey],
                        id: idsList.length > 0 ? idsList : -1,
                    };

                    continue;
                }

                // Handle numerical value (default)
                initialState[paramKey] = {
                    ...initialState[paramKey],
                    id: Number(parsedParams[paramKey]) || -1,
                };
            }
        }
    }

    return initialState;
}

// Clear all searchFilters and return them to initial state
export function clearSearchFilters(filters) {
    const initialState = JSON.parse(JSON.stringify(filters))
    for (const filterKey in initialState) {
        initialState[filterKey] = {
            ...initialState[filterKey],
            id: -1,
            text: '',
            active: false,
        };
    }

    return initialState;
}

// Create API request payload from searchFilters state
export function createPayloadFromSearchFilters(filters, pageNum = 1) {
    const requestPayload = {
        page: pageNum,
    };

    const searchFilters = JSON.parse(JSON.stringify(filters))


    for (const filterKey in searchFilters) {
        // Handle textQuery value
        if (filterKey === 'textQuery') {
            if (searchFilters[filterKey].text) {
                requestPayload[filterKey] = searchFilters[filterKey].text;
            }

            continue;
        }

        // Handle numerical value (default)
        if (searchFilters[filterKey].id !== -1) {
            requestPayload[filterKey] = searchFilters[filterKey].id;
        }
    }

    return requestPayload;
}

// Find a category by it's ID (intended to be used with GeneralDeclaration categories data)
export function findCategoryById(categoriesArray=[], neededId) {
    let result = undefined;

    for (const categoryData of categoriesArray) {
        if (Number(neededId) === Number(categoryData.id)) {
            result = categoryData;
            break;
        }

        // Recursive search inside subCategories
        if (categoryData.subCategories && categoryData.subCategories.length > 0) {
            const recursiveResult = findCategoryById(categoryData.subCategories, neededId);

            if (recursiveResult) {
                result = recursiveResult;
                break;
            }
        }
    }

    return result;
}

// Combine all relevantBenefitTypes of category and sub-categories (intended to be used with GeneralDeclaration categories data)
export function combineCategoryRelevantBenefitTypes(categoryData) {
    const relevantBenefitTypes = categoryData.relevantBenefitTypes;

    if (categoryData.subCategories.length > 0) {
        relevantBenefitTypes.push(
            ...categoryData.subCategories.reduce((resultArray, subCategoryData) => {
                resultArray.push(
                    ...combineCategoryRelevantBenefitTypes(subCategoryData)
                );

                return resultArray;
            }, [])
        );
    }

    // Filter array (keep unique values only) and return it
    return relevantBenefitTypes.filter(
        (currentElement, currentIndex, currentArray) => currentArray.indexOf(currentElement) === currentIndex
    );
}

// Get the ID of a Dacast video using its link
export function getDacastVideoId(videoLink) {
    const linkRegex = /^https?:\/\/iframe\.dacast\.com\/vod\/(.+)\/(.+)$/g;
    const linkParts = [...videoLink.matchAll(linkRegex)][0];

    if (!linkParts) {
        return null;
    }

    return `${linkParts[1]}-vod-${linkParts[2]}`;
}

// Sort an array by a specific key
export function sortArrayByKey(arrayToSort, keyToSort = 'order_num') {
    return arrayToSort.sort((a, b) => a[keyToSort] - b[keyToSort]);
}

export function fixMediaPath(src = '') {

    if (src?.startsWith('/_media')) {
        return getMediaPath(src)
    }
    if (src?.startsWith('http') && src.includes('/assets')) {
        const url = new URL(src)
        return getMediaPath(url.pathname.replace('/assets', '/media'))
    }

    return src

}

// ReactParser common options, to be used across the app
export function reactParserCommonOptions(options = {
    whitelistedTags: [],
    openLinksInNewTab: false,
}) {
    return {
        decodeEntities: true,
        transform: (node) => {
            // If "whitelistedTags" was defined, remove all other tags
            if (
                node?.type === 'tag' &&
                options?.whitelistedTags?.length > 0 &&
                !options?.whitelistedTags?.includes(node?.name)
            ) {
                return null;
            }

            // If "openLinksInNewTab" was defined, add target="_blank" to all links
            if (node?.name === 'a' && options?.openLinksInNewTab) {
                node.attribs.target = '_blank';
            }

            // Remove p tags without content (only space)
            if (node?.name === 'p' && /^\s+$/.test(node?.children?.[0]?.data)) {
                return null;
            }

            // Allow embedded content to be opened in full-screen
            if (node?.name === 'iframe') {
                node.attribs.allowfullscreen = true;
            }

            // In case of images with relative paths, pass it through CDN server
            if (node?.name === 'img') {
                node.attribs.src = fixMediaPath(node.attribs.src);
            }
        },
    };
}

// Replace placeholders in a string with values from an object
export function interpolate(inputString, replacementsObject = {}) {
    if (!inputString) return inputString
    // Create a regular expression pattern that matches placeholders like {placeholder}
    const pattern = /{([^}]+)}/g;

    // Replace each placeholder with the corresponding value from the replacementsObject
    return inputString.replace(pattern, (match, placeholder) => {
        const replacementValue = replacementsObject[placeholder];
        return replacementValue !== undefined ? replacementValue : match;
    });
}

// Formats a price to a fixed number of decimal places
export function formatPrice(price, fraction = 2) {
    return Number(price).toFixed(fraction)?.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

// Adds a currency symbol to a price
export function addCurrencySymbolToPrice(price, currency = 'ILS') {
    switch (currency) {
        case 'USD':
            return `$${price}`;

        case 'ILS':
            return `₪${price}`;
    }
}

// Formats a price to a fixed number of decimal places and adds a currency symbol
export function formatPriceWithCurrencySymbol(price, currency = 'ILS', fraction = 2) {
    if (isNaN(price)) {
        const formattedPriceWhenIsNaN = price.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return addCurrencySymbolToPrice(formattedPriceWhenIsNaN, currency)
    }
    return addCurrencySymbolToPrice(formatPrice(price, fraction), currency);
}


// Takes a list and filters out all duplicate items by a specific key
export function dropDuplicates(list, key = 'id') {
    const existingKeys = {};

    return list.filter((data) => {
        if (existingKeys[data[key]]) return false;
        existingKeys[data[key]] = true;
        return true;
    });
}

// Used to generate a random boolean value based on a percentage
export function randomBooleanValue(percentage = 50) {
    if (percentage <= 0) return false;
    if (percentage >= 100) return true;

    return (Math.random() * 100) < percentage;
}

export function getABParticipationStatus(key, percentage = 0) {
    if (percentage <= 0) return false;
    if (percentage >= 100) return true;

    let status = getLocalStorageItem(key);

    if (status === null) {
        status = `${randomBooleanValue(percentage)}`;
        setLocalStorageItem(key, status);
    }

    return status === 'true';
};

// Used to generate a Recaptcha token to be used in API requests
export function generateRecaptchaToken(onSuccess = () => {
}, onFailure = () => {
}, action = 'submit') {
    const {grecaptcha} = window;

    // TODO: Change this to ENV variable
    const SITE_KEY = '6Ldo2VMbAAAAAMIhQQEj49fYSWSLcuqSl-D7Fjh8';

    if (grecaptcha?.ready) {
        return grecaptcha.ready(
            () => grecaptcha.execute(SITE_KEY, {action}).then(onSuccess).catch(onFailure)
        );
    } else {
        return onSuccess()
    }

};

export function getUpsalePopupStatus(status){
    switch (status) {
            case 1:
                return UpsalePopupStatus.NO_ELIGBILITY_REQUIREMENTS;
            case 2:
                return UpsalePopupStatus.USED_THE_ELIGIBILITY;
            case 3:
            case 4:
            case 5:
            case 6:
            case 7:
            case 8:
                return UpsalePopupStatus.NO_INVENTORY;
    }
}

function getItemPrice(item) {
    return Number(
        item?.price ??
        item?.optionsArr?.options?.[item?.selectedOption]?.price ??
        item?.main?.price_after_discount ??
        item?.price_after_discount
    ) || 1;
}

export function createGalleryItem({ link, index, item_list_name = '', category = null , benefit = null}) {
    const baseItem = {
        index: index || 0,
        item_id: '',
        item_name: '',
        item_category: '',
        item_brand: '',
        item_variant: '',
        item_list_name: item_list_name,
        quantity: 1,
    };

    if (link?.route === 'benefit') {
        if(benefit){
            return createAnalyticsItemsList([benefit], {index, item_list_name})[0]
        }

        return {
            ...baseItem,    
            item_id: link.parameters.id,
            price: '',
        };
    }  else if (link?.route === 'category') {
        if (category) {
            return {
                ...baseItem,
                item_name: category.name,
                item_variant: 'category_page',
            };
        }
    } else if (link?.type === 'external' && link?.route) {

        const route = decodeURI(link?.route);
        const isBenefit = route.includes('/הטבות/');
        return {
            ...baseItem,
            item_name: isBenefit ? route.split('/הטבות/')[1] : route,
            item_variant: isBenefit ? 'category_page' : 'external_link',
        };
    }
    return null;
}

// Transforms a list of benefits-data into GA item objects
export function createAnalyticsItemsList(itemsList, options = {}) {
    try{
    return itemsList.map((currentItem, itemIndex) => {
        const itemTemplate = {
            index: options?.index || itemIndex,
            item_id: String(currentItem?.id || currentItem?.main?.id),
            item_name: String(currentItem?.title || currentItem?.main?.title || ''),
            price: getItemPrice(currentItem),
            quantity: Number(currentItem?.quantity) || 1,
        };

        // Item Variant
        const typeId = Number(currentItem?.main?.benefit_type || currentItem?.type);
        switch (typeId) {
            case 100:
                itemTemplate['item_variant'] = 'auto-discount';
                break;

            case 200:
                itemTemplate['item_variant'] = 'smart-wallet';
                break;

            case 300:
                itemTemplate['item_variant'] = 'free-coupon';
                break;

            case 700:
            case 1100:
                if (currentItem?.main?.url?.route?.startsWith('tel:')) {
                    itemTemplate['item_variant'] = 'call';
                    break;
                } else {
                    itemTemplate['item_variant'] = 'external-site';
                    break;
                }

            case 800:
                itemTemplate['item_variant'] = 'coupon-purchase';
                break;

            case 1000:
                itemTemplate['item_variant'] = 'direct-purchase';
                break;

            case 1200:
                itemTemplate['item_variant'] = 'internal-lead';
                break;

            case 1300:
                itemTemplate['item_variant'] = 'voucher-purchase';
                break;
        }

        if (currentItem.item_variant) {
            itemTemplate.item_variant = currentItem.item_variant;
        }
        // Item Brand
        if (currentItem?.item_brand || currentItem?.main?.item_brand) {
            itemTemplate.item_brand = currentItem.item_brand || currentItem.main.item_brand;
        }
        if (!itemTemplate.item_brand) {
            itemTemplate.item_brand = '';
        }

        const breadcrumbsList = currentItem?.breadcrumbs?.length > 0 ? currentItem?.breadcrumbs?.slice(0, -1) : [];
        breadcrumbsList.forEach((breadcrumbItem, breadcrumbIndex) => {
            const keyName = 'item_category' + (breadcrumbIndex > 0 ? breadcrumbIndex + 1 : '');
            itemTemplate[keyName] = breadcrumbItem?.title;
        });

        if (!itemTemplate.item_category) {
            itemTemplate.item_category = currentItem.item_category || currentItem.main?.item_category;
        }
        if (!itemTemplate.item_brand) {
            itemTemplate.item_brand = '';
        }

        if (options?.item_list_name) {
            itemTemplate.item_list_name = options.item_list_name;
        }
            return itemTemplate;
        });
    } catch (error) {
        console.error('Error creating analytics items list', error);
        return [];
    }
}

//returns full path of asset
export function getPath(path) {
    let Config = new AppConfig();
    return (Config.debug_mode ? Config.media_path + path : path);
}

//returns full path of asset
export function getMediaPath(path) {
    if (path && path.startsWith('http'))
        return path;

    let full_path = null;
    let Config = new AppConfig();
    if (typeof path === 'string' && path.includes('react/static') && window.location.origin === `${window.location.protocol}//localhost:3000`) {
        full_path = `${window.location.protocol}//localhost:3000${path}`;
    } else {
        full_path = Config.api.mediaUrl + path;
    }
    return full_path;
}

//check for empty objects
export function notEmptyObject(obj) {
    let empty = true;
    if (obj) {
        if (Object.keys(obj).length !== 0 && obj.constructor === Object) {
            empty = false;
        }
    }
    return empty;
}

//Convert Object to sorted array by order_num
export function convertToSortedArray(obj) {
    if (typeof obj !== 'object') {
        return [];
    }
    let sortedArr = Object.keys(obj).map(key => obj[key]);
    sortedArr = sortedArr.sort((a, b) => {
        return parseInt(a.order_num) - parseInt(b.order_num)
    });
    return sortedArr;
}

//Convert Object to sorted array by order_num
export function urlEncodeString(string) {
    let replaceObj = {
        ' ': '-',
        '&': '-',
        "'": '',
        '"': '`',
        '`': '_',
        "?": '',
        '\\': '-',
        '/': '-',
        ',': '_',
        '%': '_'
    }
    if (string && string !== '' && string !== undefined) {
        let stringArr = string.split('');
        for (var i = 0; i < stringArr.length; i++) {
            if (replaceObj[stringArr[i]] !== undefined) {
                stringArr[i] = replaceObj[stringArr[i]];
            }
        }
        return stringArr.join('');
    }
    return '';
}

//Convert Object to sorted array by order_num
export function preventBodyScroll(stop_scroll = false) {
    if (stop_scroll) {
        const scrollY = window.scrollY;
        document.body.style.position = 'fixed';
        document.body.style.top = (scrollY * -1) + 'px';
    } else {
        const scrollY = document.body.style.top;
        document.body.style.position = '';
        document.body.style.top = '';
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
    }
}

//query browser for user location
export function getUserGeoLocation(onSuccess, onFailure) {
    if ("geolocation" in navigator) {
        // check if geolocation is supported/enabled on current browser
        navigator.geolocation.getCurrentPosition(
            function success(position) {
                // for when getting location is a success
                onSuccess(position);
            },
            function error(error_message) {
                // for when getting location results in an error
                // console.error('An error has occured while retrieving location', error_message);
                onFailure(error_message);
            });
    } else {
        // geolocation is not supported
        // get your location some other way
        console.log('geolocation is not enabled on this browser');
    }
}

export function setMarkerClustersScript(clusterScriptOnLoad) {
    let cluster_script_attached = document.getElementById('google-map-cluster');
    if (cluster_script_attached === null) {
        let s = document.createElement('script');
        s.async = true;
        s.id = 'google-map-cluster';
        s.type = 'text/javascript';
        s.src = 'https://unpkg.com/@google/markerclustererplus@4.0.1/dist/markerclustererplus.min.js';
        let x = document.getElementsByTagName('script')[1];
        x.parentNode.insertBefore(s, x);

        /* listen for google map script load event */
        s.addEventListener('load', e => clusterScriptOnLoad());
    } else {
        clusterScriptOnLoad();
    }
}


function getKeyValue(map) {
    const kvs = {};

    for (const key in map) {
        if (typeof map[key] === 'object' && 'id' in map[key] && 'active' in map[key]) {
            if (map[key].id !== -1)
                kvs[key] = map[key].id
            else if (map[key].text) {
                kvs[key] = map[key].text
            }
            continue
        }

        if (Array.isArray(map[key])) {
            kvs[key] = map[key].join(',')
            continue
        }

        if (map[key] != null)
            kvs[key] = String(map[key])
    }


    return kvs
}

export function createFiltersUrl(obj) {

    return new URLSearchParams(getKeyValue(obj)).toString()

}

export function parseUrlParams(url) {
    let payload = {};

    if (url) {
        const params = Object
            .fromEntries(new URLSearchParams(url).entries())
        for (const key in params) {
            if (params[key] != null) {
                payload[key] = params[key].split(',')
            }
        }
    }
    return payload;
}

export function formatNumber(num) {
    return num.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
}

export function generateUniqueId(num) {
    const characters = "ABCDEFGHIJKLMNOPQRSTUVWYXZabcdefghijklmnopqrstuvwxyz0123456789";
    let chars = [];
    for (let i = 0; i < num; i++) {
        let char = characters.charAt(Math.floor(Math.random() * characters.length));
        chars.push(char);
    }
    return chars.join("");
}

export const getDateFromUnix = (unix) => {
    const unix_timestamp = parseInt(unix);
    const date = new Date(unix_timestamp * 1000);
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
}

export const getImageFullPath = (path) => {
    if (process.env.NODE_ENV === 'development') {
        return window.location.origin + path;
    }
    return path;
}

export const getFormattedAmount = (amount) => {
    return amount.toLocaleString("en", {
        style: 'currency',
        currency: 'ILS',
        maximumFractionDigits: 1,
        minimumFractionDigits: 0
    });
}

//<-------local storage:------>

export function setLocalStorageItem(key, item) {
    try {
        let myStorage = window.localStorage;
        myStorage.setItem(key, item);
    } catch (e) {
        throw e
    }
}

export function getLocalStorageItem(key) {
    const myStorage = window.localStorage;
    return myStorage.getItem(key);
}

export function clearLocalStorage() {
    window.localStorage.clear();
}

export function isMobileDevice() {
    let isMobile = navigator?.userAgentData?.mobile;
    return (
        isMobile || /android|iphone|ios/i.test(navigator.userAgent.toLowerCase())
    );
}
 
export function setCookie(key, value, expires) {
    expires = expires ? `expires=${expires}; ` : '';
    document.cookie = `${key}=${JSON.stringify(value)}; ${expires}domain=${window.location.hostname}; path=/;`;
}

export function getCookie(key) {
    const decodedCookie = decodeURIComponent(document.cookie);
    const splitCookies = decodedCookie.split(';');
    const formattedCookies = splitCookies.map(cookie => cookie.trim().split('='));
    return formattedCookies.filter(cookie => cookie[0] === key)[0]?.[1];
}

export function debounce(func, wait, immediate) {
    let timeout;
    return function () {
        let context = this, args = arguments;
        let later = function () {
            timeout = null;
            if (!immediate) {
                func.apply(context, args);
            }
        };
        var callNow = immediate && !timeout;
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
        if (callNow) {
            func.apply(context, args);
        }
    };
}

export const executeWithCaptcha = (cb, action) => {
    if (window.grecaptcha?.enterprise?.ready) {
        window.grecaptcha.enterprise.ready(() => {
            window.grecaptcha.enterprise.execute(window.CAPTCHA_ENTERPRISE_KEY, {action: action || 'submit'})
                .then((token) => {
                    cb(token)
                }).catch((error) => {
                    console.log("executeWithCaptcha error", error);
                    cb(null)
                })
        })
    }
    else if (window.grecaptcha?.ready) {
    window.grecaptcha.ready(() => {
        window.grecaptcha.execute( window.CAPTCHA_LEGACY_KEY, {action: action || 'submit'})
            .then((token) => {
                cb(token)
            }).catch((error) => {
                console.log("executeWithCaptcha error", error);
                    cb(null)
                })
        })
    }
}


export const hasMinutesPassed = (timestamp, minutes = 5) => {
    timestamp = isNaN(timestamp) ? 0 : Number(timestamp)
    return Date.now() > (timestamp + minutes * 60 * 1000)
}

export const isInIframe = () => {
    try {
        return window.self !== window.top;
    } catch {
        return true;
    }
};

export const resolvePopupCallback = (callback) => {
    if (!callback) {
        return null;
    }

    if (typeof callback === 'function') {
        return callback;
    }

    return typeof callback.current === 'function' ? callback.current : null;
};


// Utility function to generate test IDs
export const generateTestId = (parentId, childId) => {
    if(parent){
        return `${parentId}-${childId}`;
    }
    return childId;
  };

